import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { Tv, Radio, MonitorPlay, Clock, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getProgramStatus, sortProgramsChronological } from "@/data/mediaPrograms";
import { useEmissionsStore } from "@/data/emissionsStore";
import { useCmsSection } from "@/hooks/useCmsSection";

const labels = {
  fr: {
    tag: "Audiovisuel",
    title1: "Les Émissions ",
    titleHighlight: "du Moment",
    description: "Retrouvez les programmes politiques diffusés sur les chaînes Radios/TV nationales.",
    tv: "TV",
    radio: "Radio",
    web: "Web",
    live: "LIVE (En Direct)",
    imminent: "Dans moins d'1h",
    upcoming: "Plus tard",
    replay: "Replay",
    viewAll: "Consulter les émissions et la médiathèque",
    empty: "Aucune émission imminente.",
    finished: "Terminée",
    proposeBtn: "Proposer une émission",
    footerText: "développons notre culture politique !",
    periodicity: {
      quotidien: "Quotidien",
      hebdo: "Hebdo",
      mensuel: "Mensuel",
      ponctuel: "Spécial"
    }
  },
  ar: {
    tag: "السمعي البصري",
    title1: "البرامج ",
    titleHighlight: "الحالية",
    description: "تابعوا البرامج السياسية المبثوثة على القنوات الوطنية وابقوا على اطلاع بالأولويات الحكومية.",
    tv: "تلفزيون",
    radio: "راديو",
    web: "ويب",
    live: "مباشر",
    imminent: "في أقل من ساعة",
    upcoming: "لاحقا",
    replay: "إعادة",
    viewAll: "الاطلاع على جميع البرامج",
    empty: "لا توجد برامج حالية.",
    finished: "منتهية",
    proposeBtn: "اقتراح برنامج",
    footerText: "لنطور ثقافتنا السياسية !",
    periodicity: {
      quotidien: "يومي",
      hebdo: "أسبوعي",
      mensuel: "شهري",
      ponctuel: "خاص"
    }
  },
  zgh: {
    tag: "ⴰⵎⵙⵍⵉⵡⵍⴰⴼ",
    title1: "ⵉⵖⴰⵡⴰⵙⵏ ",
    titleHighlight: "ⵏ ⵖⵉⵍⴰ",
    description: "ⴹⴼⵕⴰⵜ ⵉⵖⴰⵡⴰⵙⵏ ⵉⵙⵔⵜⴰⵏⵏ ⴳ ⵜⴱⴰⴷⵓⵜⵉⵏ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ ⴷ ⴳⵣⵓⵜ ⵉⵙⵡⵉⵏⴳⵎⵏ ⵉⵏⴱⴰⴹⵏ.",
    tv: "ⵜⵉⵍⵉⴼⵉⵣⵢⵓⵏ",
    radio: "ⵕⵕⴰⴷⵢⵓ",
    web: "ⵡⵉⴱ",
    live: "ⴰⵙⵔⵉⴷ",
    imminent: "ⴷⴷⵓ ⵏ ⵢⴰⵜ ⵜⵙⵔⴰⴳⵜ",
    upcoming: "ⴰⴷ ⵢⴰⵛⴽ",
    replay: "ⴰⵍⵙ ⴰⵏⵏⴰⵢ",
    viewAll: "ⵥⵕ ⵎⴰⵕⵕⴰ ⵉⵖⴰⵡⴰⵙⵏ",
    empty: "ⵓⵔ ⵉⵍⵍⵉ ⴽⵔⴰ ⵖⵉⵍⴰ.",
    finished: "ⵜⵙⵎⴷ",
    proposeBtn: "ⵙⵉⴷⴼ ⴰⵖⴰⵡⴰⵙ",
    footerText: "ⴰⴷ ⵏⵙⴱⵓⵖⵍⵓ ⵜⴰⴷⵍⵙⴰ ⵏⵏⵖ ⵜⴰⵙⵔⵜⴰⵏⵜ !",
    periodicity: {
      quotidien: "ⴰⵙⵙⴰⵏ",
      hebdo: "ⴰⵏⵎⵍⴰⵙⵙ",
      mensuel: "ⴰⵢⵢⵓⵔⴰⵏ",
      ponctuel: "ⵉⵥⵍⵉⵏ"
    }
  }
};

const MediaDebatesSection = () => {
  const { lang } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;
  const cms = useCmsSection("media");

  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((v) => v + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const { emissions, fetchEmissions } = useEmissionsStore();

  useEffect(() => {
    fetchEmissions(true);
  }, [fetchEmissions]);

  const programsInfo = emissions.filter(p => p.status === "approved" || p.status === undefined);

  const processedPrograms = programsInfo.map(p => ({ 
    ...p, 
    statusTracker: getProgramStatus(p.dateIso, p.durationMinutes) 
  }));

  const activePrograms = [...processedPrograms]
    .filter(p => p.published)
    .sort(sortProgramsChronological)
    .slice(0, 8);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'tv': return <Tv className="w-5 h-5 mr-1.5 opacity-90" />;
      case 'radio': return <Radio className="w-5 h-5 mr-1.5 opacity-90" />;
      case 'web': return <MonitorPlay className="w-5 h-5 mr-1.5 opacity-90" />;
      default: return <Tv className="w-5 h-5 mr-1.5 opacity-90" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return (
          <Badge className="bg-red-600 hover:bg-red-700 text-white animate-pulse shadow-[0_0_12px_rgba(220,38,38,0.6)] font-bold px-3 py-1 text-sm border-0">
            <span className="w-2 h-2 rounded-full bg-white mr-2" />{l.live}
          </Badge>
        );
      case 'imminent':
        return (
          <Badge className="bg-orange-500 hover:bg-orange-600 text-white animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.5)] font-bold px-3 py-1 border-0">
            <Clock className="w-3.5 h-3.5 mr-1.5" />{l.imminent}
          </Badge>
        );
      case 'upcoming':
        return <Badge variant="outline" className="border-border bg-background border-dashed text-foreground">{l.upcoming}</Badge>;
      case 'finished':
        return (
          <Badge className="bg-slate-500 hover:bg-slate-600 text-white animate-pulse shadow-[0_0_10px_rgba(100,116,139,0.6)] font-bold px-3 py-1 text-sm border-0 gap-1.5">
            <span className="w-2 h-2 rounded-full bg-white" />{l.finished}
          </Badge>
        );
      default:
        return null;
    }
  };

  const getProgramImage = (prog: any) => {
    if (prog.posterUrl) return prog.posterUrl;
    switch (prog.type) {
      case 'tv': return '/images/media/tv.png';
      case 'radio': return '/images/media/radio.png';
      case 'web': return '/images/media/web.png';
      default: return '/images/media/tv.png';
    }
  };

  return (
    <section className="py-6 md:py-8 bg-background overflow-hidden border-y border-border/40">
      <div className="container relative">
        <motion.div
          className="text-center mb-8 max-w-2xl mx-auto flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-secondary/10 text-secondary font-body text-base md:text-lg font-extrabold mb-1.5 border border-secondary/20 animate-pulse tracking-widest uppercase">
            {l.tag}
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground">
            {lang === 'ar' ? (cms.title_ar ? cms.title_ar : <>{l.title1}<span className="text-gradient-warm">{l.titleHighlight}</span></>) : (cms.title_fr ? cms.title_fr : <>{l.title1}<span className="text-gradient-warm">{l.titleHighlight}</span></>)}
          </h2>
          <p className="mt-1.5 text-muted-foreground font-body text-lg leading-relaxed text-center mx-auto md:whitespace-nowrap mb-2">
            {lang === 'ar' ? (cms.subtitle_ar || l.description) : (cms.subtitle_fr || l.description)}
          </p>
          <Button variant="secondary" className="font-body font-semibold shadow-sm" asChild>
            <Link to="/proposer-emission">{l.proposeBtn}</Link>
          </Button>
        </motion.div>

        {activePrograms.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground font-body border border-dashed rounded-xl border-border">
            {l.empty}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {activePrograms.map((prog, i) => {
              const title = lang === 'fr' ? prog.frTitle : lang === 'ar' ? prog.arTitle : prog.zghTitle;
              const dateStr = lang === 'fr' ? prog.dateFormatted_fr : lang === 'ar' ? prog.dateFormatted_ar : prog.dateFormatted_zgh;
              const typeLabel = lang === 'fr' ? labels.fr[prog.type] : lang === 'ar' ? labels.ar[prog.type] : labels.zgh[prog.type];
              const periodicityLabel = l.periodicity[prog.periodicity as keyof typeof l.periodicity];

              return (
                <motion.div
                  key={prog.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`group flex flex-col h-full bg-card rounded-2xl border border-border/80 shadow-sm hover:shadow-elevated transition-all duration-300 overflow-hidden`}
                >
                  <Link to={`/emissions/${prog.id}`} className="block relative h-32 md:h-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 overflow-hidden">
                    <img src={getProgramImage(prog)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 md:group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/30 mix-blend-multiply" />
                    <div className="absolute inset-0 p-3 md:p-4 flex flex-col justify-between">
                      <div className="flex items-start justify-between w-full">
                        <Badge variant="secondary" className="backdrop-blur-md bg-white/90 text-black font-display font-bold py-1 px-2 md:py-1.5 md:px-3 uppercase tracking-wider text-[9px] md:text-[10px] shadow-sm">
                          {getTypeIcon(prog.type)}
                          <span className="hidden sm:inline">{typeLabel}</span>
                        </Badge>
                        <Badge variant="outline" className="backdrop-blur-md bg-black/40 text-white font-body text-[9px] md:text-[10px] tracking-wide uppercase border-white/20">
                          {periodicityLabel}
                        </Badge>
                      </div>

                      <div className="flex justify-end self-end w-full">
                         {getStatusBadge(prog.statusTracker)}
                      </div>
                    </div>
                  </Link>

                  <CardContent className="p-4 md:p-6 flex-1 flex flex-col">
                      <Link to={`/emissions/${prog.id}`} className="hover:underline focus:outline-none focus:underline mb-3 md:mb-4">
                        <h3 className="font-display font-bold text-lg md:text-xl text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
                          {title}
                        </h3>
                      </Link>
                      
                      <div className="mt-auto">
                        <div className="inline-flex items-center text-xs md:text-sm font-black text-primary/90 bg-primary/10 px-2 py-1 md:px-2.5 rounded-md mb-3 tracking-wide">
                          {prog.channel}
                        </div>

                        <div className="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-4 text-xs md:text-sm text-foreground font-body font-bold pt-3 border-t border-border/50">
                           <div className="flex items-center">
                              <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-primary/80 shrink-0" />
                              <span className="truncate">{dateStr}</span>
                           </div>
                           <div className="flex items-center">
                              <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-primary/80 shrink-0" />
                              <span dir="ltr">{prog.time}</span>
                           </div>
                        </div>
                      </div>
                  </CardContent>
                </motion.div>
              );
            })}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-6 md:mt-8 flex flex-col items-center"
        >
          <Button size="lg" className="font-display font-bold text-sm shadow-md" asChild>
            <Link to="/emissions">
              {l.viewAll} <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
          <p className="mt-3 text-sm font-body text-muted-foreground italic">
            {l.footerText}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MediaDebatesSection;
